/**
 * The title and the story screens as 3D scenes (spec 08 §1).
 *
 * CONTRACT (integrator-owned signatures; the TITLE stream fills in the bodies and adds files beside
 * this one). GameView shows one of them in place of the stage, as it does the ending; the menus and
 * the story's lines are drawn over it on the HUD canvas, so the scenes leave room for them (see
 * `TITLE_LAYOUT`).
 *
 * The scenes themselves are `TitleStage` and `StoryStage`; the story's text, positions and timing as
 * the C64 has them are in `storyText.ts` (re-exported here for the session's banners).
 */

import { Group, type Texture } from 'three';
import type { Palette } from '../palette';
import { createTitleMaterials, type TitleMaterials } from './materials';
import { StoryStage } from './storyStage';
import { TitleStage } from './titleStage';

export type { C64TextLine } from './c64Text';
export { STORY_FRAMES, STORY_LINES, STORY_SECONDS, storyRowY } from './storyText';

/**
 * Where the HUD draws over the title, in HUD pixels (640x360, y down): the scene keeps its logo above
 * `menuTop` and its figures clear of the band between `menuTop` and `menuBottom` in the middle
 * `menuWidth` pixels.
 */
export const TITLE_LAYOUT = { menuTop: 176, menuBottom: 300, menuWidth: 224 } as const;

/** What a title scene is built from: the game's palette and the toon gradient every figure uses. */
export interface TitleKit {
  palette: Palette;
  gradientMap: Texture;
}

/** The title: the BUBBLE BOBBLE logo, Bub and Bob, bubbles rising. */
export class TitleView {
  readonly group = new Group();
  private readonly materials: TitleMaterials;
  private readonly stage: TitleStage;

  constructor(kit: TitleKit) {
    this.materials = createTitleMaterials(kit.palette, kit.gradientMap);
    this.stage = new TitleStage(kit.palette, this.materials);
    this.group.add(this.stage.group);
  }

  /** `t`: seconds since the title appeared. */
  update(t: number): void {
    this.stage.update(t);
  }

  dispose(): void {
    this.stage.dispose();
    for (const m of this.materials.all) m.dispose();
  }
}

/** The C64's story screen before level 1: its text is the session's banners; the scene is behind it. */
export class StoryView {
  readonly group = new Group();
  private readonly materials: TitleMaterials;
  private readonly stage: StoryStage;

  constructor(kit: TitleKit) {
    this.materials = createTitleMaterials(kit.palette, kit.gradientMap);
    this.stage = new StoryStage(kit.palette, this.materials);
    this.group.add(this.stage.group);
  }

  /** `t`: seconds since the story screen appeared (the C64 holds it `STORY_SECONDS`). */
  update(t: number): void {
    this.stage.update(t);
  }

  dispose(): void {
    this.stage.dispose();
    for (const m of this.materials.all) m.dispose();
  }
}
